import Chart from 'chart.js/auto';
import {
    buildFilterSelects,
    attachTimeListeners,
    attachFilterListeners,
    hasActiveFilters,
    getVisibleCombos,
    getActiveSegmentKey,
    normalizeData,
    hoverLinePlugin,
} from './mmi-chart-shared.js';

(function () {
    const panel = document.querySelector('[data-mmi-employment-chart]');
    if (!panel) return;

    const extraPoint = (() => {
        try {
            const parsed = JSON.parse(panel.dataset.extraPoint || 'null');
            return parsed && typeof parsed === 'object' ? parsed : null;
        } catch {
            return null;
        }
    })();

    const canvas = panel.querySelector('canvas');
    const timeButtons = panel.querySelectorAll('[data-time-filter]');

    let rawData = [];
    let rowMap = {};
    let activeTime = '2yr';
    let currentIsExtra = [];
    let activeFilters = {
        region: '',
        band: '',
        industry: '',
        ownershipType: '',
    };
    let chartInstance = null;

    // ── Data processing ──────────────────────────────────────────────────────

    function computeChartData() {
        const { visibleCombos } = getVisibleCombos(rawData, activeTime);
        const segmentKey = getActiveSegmentKey(activeFilters);
        const valueKey = segmentKey || 'National';

        const labels = [];
        const values = [];
        const isExtra = [];
        const adpSmallValues = [];
        const adpLargeValues = [];

        for (const k of visibleCombos) {
            const [y, q] = k.split('|');
            const row = rowMap[k];
            labels.push(`${y} Q${q}`);
            const v = row ? parseFloat(row[valueKey]) : NaN;
            values.push(isNaN(v) ? null : v);
            isExtra.push(false);
            const adpS = row ? parseFloat(row['ADP Small Business']) : NaN;
            adpSmallValues.push(isNaN(adpS) ? null : adpS);
            const adpL = row ? parseFloat(row['ADP Large Corp']) : NaN;
            adpLargeValues.push(isNaN(adpL) ? null : adpL);
        }

        // Append optional extra data point only when no category filters are active
        if (extraPoint && !hasActiveFilters(activeFilters)) {
            const label = extraPoint.label || `${extraPoint.year} Q${extraPoint.quarter}`;
            labels.push(label);
            values.push(parseFloat(extraPoint.value));
            isExtra.push(true);
            adpSmallValues.push(null);
            adpLargeValues.push(null);
        }

        return { labels, values, isExtra, adpSmallValues, adpLargeValues };
    }

    // ── Chart rendering ──────────────────────────────────────────────────────

    const LINE_COLOR = '#EE3325';
    const EXTRA_COLOR = '#ffffff';
    const ADP_SMALL_COLOR = '#56BCEB';
    const ADP_LARGE_COLOR = '#082B41';

    function pointStyles(values, isExtra) {
        const bg = values.map((v, i) => isExtra[i] ? EXTRA_COLOR : LINE_COLOR);
        const radius = values.map((v, i) => isExtra[i] ? 6 : 3);
        return { bg, radius };
    }

    function renderChart() {
        const { labels, values, isExtra, adpSmallValues, adpLargeValues } = computeChartData();
        const { bg: ptBg, radius: ptRadius } = pointStyles(values, isExtra);
        currentIsExtra = isExtra;

        if (chartInstance) {
            chartInstance.data.labels = labels;
            const ds = chartInstance.data.datasets[0];
            ds.data = values;
            ds.pointBackgroundColor = ptBg;
            ds.pointRadius = ptRadius;
            chartInstance.data.datasets[1].data = adpSmallValues;
            chartInstance.data.datasets[2].data = adpLargeValues;
            chartInstance.update();
            return;
        }

        chartInstance = new Chart(canvas, {
            type: 'line',
            plugins: [hoverLinePlugin],
            data: {
                labels,
                datasets: [
                    {
                        label: 'Middle Market Indicator',
                        data: values,
                        borderColor: LINE_COLOR,
                        backgroundColor: 'transparent',
                        pointBackgroundColor: ptBg,
                        pointRadius: ptRadius,
                        pointHoverRadius: 5,
                        fill: false,
                        tension: 0.3,
                        borderWidth: 2,
                        spanGaps: true,
                        segment: {
                            borderDash: ctx => currentIsExtra[ctx.p1DataIndex] ? [6, 4] : undefined,
                        },
                    },
                    {
                        label: 'ADP Small Business (%)',
                        data: adpSmallValues,
                        borderColor: ADP_SMALL_COLOR,
                        backgroundColor: 'transparent',
                        pointBackgroundColor: ADP_SMALL_COLOR,
                        pointRadius: 3,
                        pointHoverRadius: 5,
                        fill: false,
                        tension: 0.3,
                        borderWidth: 2,
                        spanGaps: true,
                    },
                    {
                        label: 'ADP Large Corp (%)',
                        data: adpLargeValues,
                        borderColor: ADP_LARGE_COLOR,
                        backgroundColor: 'transparent',
                        pointBackgroundColor: ADP_LARGE_COLOR,
                        pointRadius: 3,
                        pointHoverRadius: 5,
                        fill: false,
                        tension: 0.3,
                        borderWidth: 2,
                        spanGaps: true,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                animation: { duration: 500, easing: 'easeInOutQuart' },
                plugins: {
                    legend: {
                        display: true,
                        position: 'bottom',
                        align: 'start',
                        title: {
                            display: true,
                            text: ' ',
                            padding: { bottom: 40 },
                        },
                        labels: {
                            usePointStyle: true,
                            pointStyle: 'circle',
                            boxHeight: 8,
                            padding: 20,
                        },
                    },
                    tooltip: {
                        backgroundColor: '#ffffff',
                        borderColor: '#082B41',
                        borderWidth: 1,
                        padding: 20,
                        usePointStyle: true,
                        titleColor: '#082B41',
                        bodyColor: '#082B41',
                        footerColor: '#082B41',
                        callbacks: {
                            labelPointStyle: () => ({
                                pointStyle: 'circle',
                                rotation: 0,
                            }),
                            label: ctx => {
                                const v = ctx.parsed.y;
                                return v !== null ? `${ctx.dataset.label}: ${v > 0 ? '+' : ''}${v}%` : `${ctx.dataset.label}: No data`;
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        grid: { display: false },
                        ticks: {
                            autoSkip: true,
                            maxRotation: 0,
                            minRotation: 0,
                            font: { size: 11 },
                        },
                    },
                    y: {
                        border: { display: false },
                        grid: {
                            color: ctx => ctx.tick.value === 0 ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.07)',
                            lineWidth: ctx => ctx.tick.value === 0 ? 2 : 1,
                        },
                        ticks: {
                            callback: v => `${v > 0 ? '+' : ''}${v}%`,
                            font: { size: 11 },
                        },
                    },
                },
            },
        });
    }

    // ── Inline data ──────────────────────────────────────────────────────────

    const dataScript = document.getElementById('mmi-data-employment');
    if (!dataScript) return;
    try {
        rawData = normalizeData(JSON.parse(dataScript.textContent));
    } catch (err) {
        console.error('[mmi-employment-chart] Failed to parse inline data:', err);
        return;
    }
    if (!rawData.length) return;
    rowMap = Object.fromEntries(rawData.map(d => [`${d.Year}|${d.Quarter}`, d]));
    buildFilterSelects(panel, rawData);
    attachTimeListeners(timeButtons, time => { activeTime = time; renderChart(); });
    attachFilterListeners(panel, activeFilters, renderChart);
    renderChart();
})();
